import { FC } from 'react'
import shallow from 'zustand/shallow'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import { usePyramidForecastValues } from '../../graphql/pyramids/hooks/usePyramidForecast'
import { LoadingAndErrors } from './../../components/custom_components/LoadingAndErrors'
import usePyramidValuesAndReferencePyramids from './../../graphql/pyramids/hooks/usePyramidValuesAndReferencePyramids'
import './pyramids.less'
import { PyramidValueDto, PyramidType } from '../../types'
import { PyramidsInfoBox } from './shared/PyramidsInfoBox'
import usePyramidsPastValue from '../../graphql/pyramids/hooks/usePyramidsPastValue'
import {
  formatPyramidReference,
  formatPyramidValues,
  formatPyramidValuesForColumnGraph,
  formatPyramidValuesForGraphAndTable,
} from './pyramidUtils'
import { usePyramidsStore } from './PyramidsStore'
import { PyramidsContentLayout } from './shared/PyramidsContentLayout'
import { PyramidsTable } from './shared/PyramidsTable'
import { PyramidsColumnGraph } from './visualization/PyramidsColumnGraph'
import { useEmployeeLevelsByCountry } from '../../graphql/employee/hooks/useEmployeeLevelsByCountry'
import { useTranslation } from 'react-i18next'

interface Props {
  type: PyramidType
}

export const PyramidsTab: FC<Props> = ({ type }) => {
  const { t } = useTranslation()
  const [pastDate, forecastDate, showPast, showCurrent, showForecast, selected] = usePyramidsStore(
    (state) => [state.pastDate, state.forecastDate, state.showPast, state.showCurrent, state.showForecast, state.selected],
    shallow,
  )

  const companyId = selected.companyId
  const country = selected.country

  const { data, loading, error } = usePyramidValuesAndReferencePyramids(companyId, country, type)

  const {
    data: pastData,
    error: pastError,
    loading: pastLoading,
  } = usePyramidsPastValue(companyId, country, type, pastDate.year, pastDate.month)

  const {
    data: forecastData,
    error: forecastError,
    loading: forecastLoading,
  } = usePyramidForecastValues(companyId, country, forecastDate.year, forecastDate.month)

  const { data: employeeLevelsData, error: employeeLevelsError, loading: employeeLevelsLoading } = useEmployeeLevelsByCountry(country)

  if (
    !data ||
    !pastData ||
    !forecastData ||
    !employeeLevelsData ||
    !data.pyramidValues ||
    !pastData.pyramidPastValues ||
    !forecastData.pyramidForecastValues ||
    !employeeLevelsData.employeeLevels ||
    error ||
    pastError ||
    forecastError ||
    employeeLevelsError
  ) {
    return (
      <LoadingAndErrors
        isLoading={loading || pastLoading || forecastLoading || employeeLevelsLoading}
        hasNoData={
          !data ||
          !data.pyramidValues ||
          !pastData ||
          !pastData.pyramidPastValues ||
          !forecastData ||
          !forecastData.pyramidForecastValues ||
          !employeeLevelsData ||
          !employeeLevelsData.employeeLevels
        }
        error={error || pastError || forecastError}
      />
    )
  }

  const pyramidValues: PyramidValueDto[] = formatPyramidValues(data.pyramidValues, country, type, employeeLevelsData.employeeLevels)
  const pastPyramidValues: PyramidValueDto[] = formatPyramidValues(
    pastData.pyramidPastValues,
    country,
    type,
    employeeLevelsData.employeeLevels,
  )
  const recommendedValues =
    data.referencePyramid && data.referencePyramid.referencePyramidDetails
      ? formatPyramidReference(data.referencePyramid.referencePyramidDetails, country, type, employeeLevelsData.employeeLevels)
      : undefined

  const tableData = recommendedValues ? [...recommendedValues].sort((x, y) => x.displayOrder - y.displayOrder) : []

  const pyramidsData = formatPyramidValuesForGraphAndTable(
    pyramidValues,
    pastPyramidValues,
    forecastData.pyramidForecastValues,
    tableData,
    type,
  )

  const graphData = formatPyramidValuesForColumnGraph(
    pyramidValues,
    pastPyramidValues,
    forecastData.pyramidForecastValues,
    tableData,
    type,
  )

  return (
    <BackgroundBox r noPadding color="transparent" height="withTabs">
      <PyramidsInfoBox companyId={companyId} country={country} infoType={type} />
      <PyramidsContentLayout
        type={type}
        graph={
          <PyramidsColumnGraph
            type={type}
            data={graphData}
            variant="pyramidsView"
            chartAriaLabel={t(`pyramidsView.shared.graph.heading.${type}`)}
            showPast={showPast}
            showCurrent={showCurrent}
            showForecast={showForecast}
          />
        }
        overview={pyramidValues && <PyramidsTable tableData={pyramidsData} type={type} variant="pyramidsView" />}
      />
    </BackgroundBox>
  )
}
